import React from 'react';
import PageHeader from '../components/PageHeader';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  const lastUpdated = 'January 1, 2025';

  return (
    <div className="page-enter">
      <PageHeader title="Privacy Policy" breadcrumbs={[{ label: 'Privacy Policy' }]} />

      <div className="py-14 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-sm text-muted-foreground mb-8">Last Updated: {lastUpdated}</p>

          <div className="prose max-w-none space-y-8 text-muted-foreground text-sm leading-relaxed">

            <section>
              <h2 className="text-xl font-bold text-foreground mb-3 pb-2 border-b border-border">1. Introduction</h2>
              <p>
                B2BTechLeads ("Company," "we," "us," or "our") is committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website <strong>b2btechleads.com</strong> or use our services.
              </p>
              <p className="mt-3">
                Please read this policy carefully. If you disagree with its terms, please discontinue use of our site. If you have any questions, contact us at <a href="mailto:info@b2btechleads.com" style={{ color: '#43a047' }} className="hover:underline">info@b2btechleads.com</a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mb-3 pb-2 border-b border-border">2. Information We Collect</h2>
              <h3 className="font-semibold text-foreground mb-2">Personal Information You Provide</h3>
              <p>We collect personal information that you voluntarily provide when you:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Fill out our contact form (name, email, phone, company name, message)</li>
                <li>Subscribe to our newsletter or marketing communications</li>
                <li>Request a quote, brochure, or white paper</li>
                <li>Engage with our sales team or account managers</li>
              </ul>

              <h3 className="font-semibold text-foreground mt-4 mb-2">Information Collected Automatically</h3>
              <p>When you visit our website, we may automatically collect:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>IP address and approximate geographic location</li>
                <li>Browser type, version, and operating system</li>
                <li>Pages visited, time spent, and referring URLs</li>
                <li>Device identifiers and cookie data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mb-3 pb-2 border-b border-border">3. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Respond to your inquiries and fulfill service requests</li>
                <li>Send you marketing and promotional communications (with your consent)</li>
                <li>Improve and optimize our website and services</li>
                <li>Analyze usage trends and user engagement</li>
                <li>Prevent fraud and ensure the security of our platform</li>
                <li>Comply with applicable laws and regulations</li>
                <li>Send administrative notices and service updates</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mb-3 pb-2 border-b border-border">4. Sharing of Your Information</h2>
              <p>We may share your personal information in the following situations:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Service Providers:</strong> With third-party vendors who assist us in operating our website, conducting our business, or servicing you (e.g., email providers, CRM platforms, analytics tools).</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of all or a portion of our assets.</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority.</li>
                <li><strong>With Your Consent:</strong> We may share your data for any other purpose with your explicit consent.</li>
              </ul>
              <p className="mt-3">
                We do <strong>not</strong> sell, trade, or rent your personal information to unaffiliated third parties for their own marketing purposes without your explicit consent. To learn more about opting out, see our <Link to="/do-not-sell" style={{ color: '#43a047' }} className="hover:underline">Do Not Sell My Personal Information</Link> page.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mb-3 pb-2 border-b border-border">5. Cookies and Tracking Technologies</h2>
              <p>
                We use cookies, web beacons, and similar tracking technologies to collect and store information about your interactions with our website. Cookies are small data files placed on your device that allow us to recognize your browser and capture certain information.
              </p>
              <p className="mt-3">You can set your browser to refuse cookies or alert you when cookies are being sent. However, some parts of our website may not function properly without cookies.</p>
              <p className="mt-3">Types of cookies we use:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Essential Cookies:</strong> Required for the website to function properly.</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website.</li>
                <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements and track campaign effectiveness.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mb-3 pb-2 border-b border-border">6. Data Retention</h2>
              <p>
                We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law. When we no longer need your information, we will securely delete or anonymize it.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mb-3 pb-2 border-b border-border">7. Your Privacy Rights</h2>
              <p>Depending on your location, you may have the following rights:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you.</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data.</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information ("right to be forgotten").</li>
                <li><strong>Opt-Out of Sale:</strong> Direct us not to sell your personal information. See <Link to="/do-not-sell" style={{ color: '#43a047' }} className="hover:underline">Do Not Sell My Personal Information</Link>.</li>
                <li><strong>Data Portability:</strong> Request transfer of your data in a machine-readable format.</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent.</li>
              </ul>
              <p className="mt-3">To exercise any of these rights, email us at <a href="mailto:info@b2btechleads.com" style={{ color: '#43a047' }} className="hover:underline">info@b2btechleads.com</a>.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mb-3 pb-2 border-b border-border">8. California Privacy Rights (CCPA)</h2>
              <p>
                If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA). These include the right to know what personal information we collect, the right to delete your personal information, and the right to opt out of the sale of your personal information.
              </p>
              <p className="mt-3">
                To submit a verifiable consumer request, please visit our <Link to="/do-not-sell" style={{ color: '#43a047' }} className="hover:underline">Do Not Sell My Personal Information</Link> page or contact us at <a href="mailto:info@b2btechleads.com" style={{ color: '#43a047' }} className="hover:underline">info@b2btechleads.com</a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mb-3 pb-2 border-b border-border">9. Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no internet transmission or electronic storage method is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mb-3 pb-2 border-b border-border">10. Third-Party Links</h2>
              <p>
                Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of those sites. We encourage you to review the privacy policies of any third-party sites you visit.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mb-3 pb-2 border-b border-border">11. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of material changes by posting the new policy on this page with an updated "Last Updated" date. Your continued use of our website after changes are posted constitutes your acceptance of the revised policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-foreground mb-3 pb-2 border-b border-border">12. Contact Us</h2>
              <p>If you have questions or concerns about this Privacy Policy, please contact us:</p>
              <div className="mt-3 bg-muted border border-border p-5 rounded-sm">
                <p className="font-semibold text-foreground">B2BTechLeads</p>
                <p>15 Business Ave, Suite 500, Orlando, FL 34236</p>
                <p>Phone: <a href="tel:941-799-8809" style={{ color: '#43a047' }} className="hover:underline">941-799-8809</a></p>
                <p>Email: <a href="mailto:info@b2btechleads.com" style={{ color: '#43a047' }} className="hover:underline">info@b2btechleads.com</a></p>
              </div>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
